import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as EyeIcon } from "components/Icons/eye.svg";
import { ReactComponent as Pencil02Icon } from "components/Icons/pencil-02.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import {
  AttributeType,
  SchemaAttribute,
  SchemaAttributeType,
} from "types/customEvents";
import { JsonEditor } from "./JsonEditor/JsonEditor";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    displayBox: {
      borderRadius: "4px",
      border: `1px solid ${theme.gray200}`,
      background: theme.white00,
      minWidth: "50px",
      display: "flex",
      alignItems: "center",
      height: "32px",
      justifyContent: "center",
      padding: theme.spacing(3, 5),
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
    wrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: theme.spacing(6),
    },
    content: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(4),
      maxWidth: "calc(100% - 110px)",
      flexWrap: "wrap",
    },
    actions: {
      display: "flex",
      alignItems: "center",
      width: "100px",
    },
    iconBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "50%",
      width: "24px",
      minWidth: theme.spacing(12),
      height: "24px",
      background: theme.gray50,
      margin: theme.spacing(0, 2),
      cursor: "pointer",
    },
    icon: {
      width: "16px",
      height: "16px",
      color: theme.gray500,
    },
    error: {
      border: `1px solid ${theme.red300}`,
      backgroundColor: theme.red50,
      color: theme.red800,
    },
    title: {
      whiteSpace: "nowrap",
    },
    text: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      alignItems: "center",
    },
    viewCode: {
      display: "flex",
      alignItems: "center",
      color: theme.primary500,
      lineHeight: "20px",
      cursor: "pointer",

      "& svg": {
        marginRight: theme.spacing(4),
      },
    },
  }),
);

interface Props {
  index: number;
  name: string;
  description: string;
  type: SchemaAttributeType;
  remove: (index: number) => void;
  add: (attribute: AttributeType) => void;
  edit: () => void;
  error?: {
    name: string[];
    type: string[];
    description: string[];
  };
  isComplexAttribute: boolean;
  attributeData: SchemaAttribute;
  setFieldValue: (value: any) => void;
}

export const AttributeReadOnly = ({
  name,
  description,
  type,
  index,
  remove,
  add,
  edit,
  error,
  isComplexAttribute,
  attributeData,
  setFieldValue,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { message } = useMessage();
  const classes = useStyles();
  const nameError = error?.name?.join();
  const typeError = error?.type?.join();
  const descriptionError = error?.description?.join();

  return (
    <div className={classes.wrapper}>
      <OLModal
        disableEscapeKeyDown
        open={isOpen}
        onClose={() => setIsOpen(false)}
        modalWidth={550}
      >
        <JsonEditor
          json={attributeData}
          closeModal={() => setIsOpen(false)}
          setFieldValue={setFieldValue}
          onSave={() => setIsOpen(false)}
        />
      </OLModal>
      <div className={classes.content}>
        <span>{index + 1}.</span>
        {isComplexAttribute ? (
          <>
            <span>{message({ id: "schemaAttributeComplex" })}</span>
            <span className={classes.viewCode} onClick={() => setIsOpen(true)}>
              <EyeIcon />
              {message({ id: "viewCode" })}
            </span>
          </>
        ) : (
          <>
            <span className={classes.title}>{message({ id: "systemId" })}</span>
            <Tooltip text={nameError || ""}>
              <div
                className={`${classes.displayBox} ${nameError && classes.error}`}
              >
                <div className={classes.text}>{name}</div>
              </div>
            </Tooltip>

            <span className={classes.title}>{message({ id: "type" })}</span>
            <Tooltip text={typeError || ""}>
              <div
                className={`${classes.displayBox} ${typeError && classes.error}`}
              >
                <div className={classes.text}>{message({ id: type })}</div>
              </div>
            </Tooltip>
            {description && (
              <>
                <span className={classes.title}>{message({ id: "name" })}</span>
                <Tooltip text={descriptionError || ""}>
                  <div
                    className={`${classes.displayBox} ${
                      descriptionError && classes.error
                    }`}
                  >
                    <div className={classes.text}>{description}</div>
                  </div>
                </Tooltip>
              </>
            )}
          </>
        )}
      </div>

      <div className={classes.actions}>
        <div className={classes.iconBox}>
          <Pencil02Icon
            onClick={() => (isComplexAttribute ? setIsOpen(true) : edit())}
            className={classes.icon}
          />
        </div>

        <div className={classes.iconBox}>
          <Copy02Icon
            onClick={() => {
              const data = isComplexAttribute
                ? attributeData
                : {
                    name,
                    description,
                    type,
                  };

              add({
                isEditing: true,
                isSaved: false,
                draft: {
                  ...data,
                },
                ...data,
              });
            }}
            className={classes.icon}
          />
        </div>

        <div className={classes.iconBox}>
          <XCloseIcon onClick={() => remove(index)} className={classes.icon} />
        </div>
      </div>
    </div>
  );
};
